import { buildLabelCommands, bytesToBase64, type LabelCommandLanguage } from './label-commands';

export const PRINT_BRIDGE_DEFAULT_PORT = 9101;
const PROBE_TIMEOUT_MS = 4000;

export interface PrintBridgePrinter {
  name: string;
  offline: boolean;
}

export interface PrintBridgeHealth {
  status: string;
  version?: string;
  defaultPrinter?: string | null;
  printers?: PrintBridgePrinter[];
}

export interface PrintBridgeResult {
  ok: boolean;
  error?: string;
  printer?: string;
}

export interface LabelSettings {
  widthMm: number;
  heightMm: number;
  language: LabelCommandLanguage;
  port: number;
}

export const DEFAULT_LABEL_SETTINGS: LabelSettings = { widthMm: 50, heightMm: 25, language: 'TSPL', port: PRINT_BRIDGE_DEFAULT_PORT };

export function labelSettingsFrom(settings: Record<string, unknown> | undefined): LabelSettings {
  const s = settings ?? {};
  return {
    widthMm: Number(s.labelWidthMm) || DEFAULT_LABEL_SETTINGS.widthMm,
    heightMm: Number(s.labelHeightMm) || DEFAULT_LABEL_SETTINGS.heightMm,
    language: s.labelCommandLang === 'ESCPOS' ? 'ESCPOS' : 'TSPL',
    port: Number(s.printBridgePort) || DEFAULT_LABEL_SETTINGS.port,
  };
}

const baseUrl = (port: number) => `http://127.0.0.1:${port}`;

export async function probePrintBridge(port = PRINT_BRIDGE_DEFAULT_PORT): Promise<PrintBridgeHealth | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${baseUrl(port)}/health`, { signal: controller.signal });
    if (!res.ok) return null;
    return (await res.json()) as PrintBridgeHealth;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

export async function sendToPrintBridge(input: { port?: number; printer?: string; dataBase64: string; jobName?: string }): Promise<PrintBridgeResult> {
  const port = input.port ?? PRINT_BRIDGE_DEFAULT_PORT;
  try {
    const res = await fetch(`${baseUrl(port)}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dataBase64: input.dataBase64, printer: input.printer, jobName: input.jobName }),
    });
    const payload = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; printer?: string };
    if (!res.ok || payload.ok === false) return { ok: false, error: payload.error ?? `Print bridge returned ${res.status}` };
    return { ok: true, printer: payload.printer };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Print bridge unreachable' };
  }
}

export function printLabels(items: { barcode: string; topText?: string }[], settings: LabelSettings, printer?: string): Promise<PrintBridgeResult> {
  const bytes = buildLabelCommands(settings.language, items, {
    widthMm: settings.widthMm,
    heightMm: settings.heightMm,
    marginXMm: 2,
    marginYMm: 2,
  });
  return sendToPrintBridge({ port: settings.port, printer, dataBase64: bytesToBase64(bytes), jobName: 'his-specimen-labels' });
}
